/**
 * A tiny keyed localStorage store: a `Record<string, T>` persisted under one
 * JSON key, with corruption-safe reads (bad JSON / non-object → `{}`),
 * quota-safe writes (failures are no-ops), and SSR/no-window guards. Backs the
 * per-tool and per-indicator user-default stores, which were previously two
 * verbatim copies of this read/write boilerplate.
 *
 * Local-only by design — no network round-trip. If server-side sync is ever
 * wanted, this is the single seam to swap out.
 */
export interface KeyedLocalStore<T> {
  /** The whole map (empty when unset/corrupt/no-window). */
  readAll(): Record<string, T>
  get(key: string): T | undefined
  set(key: string, value: T): void
  clear(key: string): void
  /** Overwrite the whole map (server-wins sync path). */
  replaceAll(next: Record<string, T>): void
}

export function createLocalStore<T>(storageKey: string): KeyedLocalStore<T> {
  function readAll(): Record<string, T> {
    if (typeof window === 'undefined') return {}
    try {
      const raw = window.localStorage.getItem(storageKey)
      if (!raw) return {}
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, T>
      }
    } catch {
      /* corrupted — drop it silently and start fresh */
    }
    return {}
  }

  function writeAll(next: Record<string, T>): void {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next))
    } catch {
      /* quota or denied — best effort, no-op */
    }
  }

  return {
    readAll,
    get: (key) => readAll()[key],
    set: (key, value) => {
      const store = readAll()
      store[key] = value
      writeAll(store)
    },
    clear: (key) => {
      const store = readAll()
      if (key in store) {
        delete store[key]
        writeAll(store)
      }
    },
    replaceAll: (next) => {
      writeAll(next)
    },
  }
}
